import * as React from 'react'
import { Search, X, ChevronDown, SlidersHorizontal, Calendar, Check, Phone } from 'lucide-react'
import type { Table } from '@tanstack/react-table'
import type {
  AppointmentAttribution,
  AppointmentStatus,
  AppointmentView,
  AppointmentFiltersResponse,
} from '@/lib/api-client'

export type DatePreset = 'all' | 'today' | 'tomorrow' | 'last7days' | 'month' | 'custom'

interface AppointmentToolbarProps<TData> {
  search: string
  onSearchChange: (value: string) => void

  datePreset: DatePreset
  onDatePresetChange: (value: DatePreset) => void
  customFrom: string
  customTo: string
  onCustomFromChange: (value: string) => void
  onCustomToChange: (value: string) => void

  statuses: AppointmentStatus[]
  onStatusesChange: (statuses: AppointmentStatus[]) => void

  attribution: AppointmentAttribution
  onAttributionChange: (value: AppointmentAttribution) => void

  creator: string | null
  onCreatorChange: (value: string | null) => void
  doctor: string | null
  onDoctorChange: (value: string | null) => void

  view: AppointmentView
  onViewChange: (value: AppointmentView) => void

  includeTasks: boolean
  onIncludeTasksChange: (value: boolean) => void

  filtersData: AppointmentFiltersResponse | undefined

  table: Table<TData>
}

const DATE_PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom...' },
]

const STATUS_OPTIONS: { value: AppointmentStatus; label: string }[] = [
  { value: 'turn_up', label: 'Turn Up' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'did_not_turn_up', label: 'No-flip (DNTU)' },
]

const ATTRIBUTION_OPTIONS: { value: AppointmentAttribution; label: string }[] = [
  { value: 'all', label: 'All Sources' },
  { value: 'closer', label: '🎯 Closer (HQ)' },
  { value: 'in-clinic', label: '🏥 In-clinic' },
]

const COLUMN_LABELS: Record<string, string> = {
  appointmentAt: 'Date / Time',
  doctor: 'Doctor',
  patientName: 'Patient',
  patientType: 'Type',
  status: 'Status',
  createdBy: 'Created By',
  invoiceNo: 'Invoice',
  visitReasonCategory: 'Visit Reason',
  branchName: 'Branch',
  timeIn: 'Time In',
  patientNric: 'NRIC',
  actions: 'Actions',
}

function FilterPill({
  open,
  onToggle,
  icon,
  label,
  width,
  children,
}: {
  open: boolean
  onToggle: () => void
  icon?: React.ReactNode
  label: React.ReactNode
  width: string
  children: React.ReactNode
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center justify-between gap-2 bg-card rounded-full px-3.5 py-2 ${width} border border-border transition-all hover:bg-hover`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <span className="text-[13px] font-normal text-text-dark truncate">{label}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-foreground flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={onToggle} />
          <div className="absolute right-0 top-full mt-1 min-w-[200px] rounded-lg border border-border bg-card p-1 shadow-lg z-50 max-h-[400px] overflow-y-auto">
            {children}
          </div>
        </>
      )}
    </div>
  )
}

export function AppointmentToolbar<TData>({
  search,
  onSearchChange,
  datePreset,
  onDatePresetChange,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  statuses,
  onStatusesChange,
  attribution,
  onAttributionChange,
  creator,
  onCreatorChange,
  doctor,
  onDoctorChange,
  view,
  onViewChange,
  includeTasks,
  onIncludeTasksChange,
  filtersData,
  table,
}: AppointmentToolbarProps<TData>) {
  const [openPanel, setOpenPanel] = React.useState<string | null>(null)
  const togglePanel = (k: string) => setOpenPanel(openPanel === k ? null : k)

  const dateLabel = DATE_PRESET_OPTIONS.find((o) => o.value === datePreset)?.label ?? 'All Time'
  const statusLabel =
    statuses.length === 0 ? 'Status' : statuses.length === 1 ? STATUS_OPTIONS.find((s) => s.value === statuses[0])?.label : `${statuses.length} statuses`
  const attributionLabel = ATTRIBUTION_OPTIONS.find((o) => o.value === attribution)?.label ?? 'All Sources'

  const toggleStatus = (s: AppointmentStatus) => {
    onStatusesChange(statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s])
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Top row: search + view toggle */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 bg-card rounded-full px-3.5 py-2 w-full sm:w-[280px] border border-border transition-all focus-within:ring-2 focus-within:ring-info/20">
          <Search className="w-4 h-4 text-foreground flex-shrink-0" />
          <input
            type="text"
            placeholder="Search patient, PNO, NRIC..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="border-none outline-none text-[13px] font-normal w-full bg-transparent text-text-dark placeholder:text-text-muted"
          />
          {search && (
            <button onClick={() => onSearchChange('')} className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-hover">
              <X className="w-3.5 h-3.5 text-text-muted" />
            </button>
          )}
        </div>

        {/* View toggle: Rows / Patients */}
        <div className="inline-flex rounded-full border border-border bg-card p-0.5 self-start sm:self-auto">
          <button
            onClick={() => onViewChange('rows')}
            className={`px-4 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
              view === 'rows' ? 'bg-info text-white' : 'text-text-muted hover:text-text-dark'
            }`}
          >
            Rows
          </button>
          <button
            onClick={() => onViewChange('patients')}
            className={`px-4 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
              view === 'patients' ? 'bg-info text-white' : 'text-text-muted hover:text-text-dark'
            }`}
          >
            Patients
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2.5">
        {/* Date preset */}
        <FilterPill
          open={openPanel === 'date'}
          onToggle={() => togglePanel('date')}
          icon={<Calendar className="w-4 h-4 text-foreground flex-shrink-0" />}
          label={dateLabel}
          width="w-[140px]"
        >
          {DATE_PRESET_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onDatePresetChange(opt.value)
                if (opt.value !== 'custom') setOpenPanel(null)
              }}
              className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                datePreset === opt.value ? 'bg-hover text-text-dark font-medium' : 'text-text-dark hover:bg-hover'
              }`}
            >
              {opt.label}
              {datePreset === opt.value && <Check className="w-3.5 h-3.5 text-success" />}
            </button>
          ))}
          {datePreset === 'custom' && (
            <div className="border-t border-border mt-1 pt-2 px-2 pb-1 space-y-2">
              <div>
                <label className="text-[10px] uppercase text-text-muted">From</label>
                <input type="date" value={customFrom} onChange={(e) => onCustomFromChange(e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-[12px]" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-text-muted">To</label>
                <input type="date" value={customTo} onChange={(e) => onCustomToChange(e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-[12px]" />
              </div>
            </div>
          )}
        </FilterPill>

        {/* Status multi-select */}
        <FilterPill
          open={openPanel === 'status'}
          onToggle={() => togglePanel('status')}
          label={statusLabel}
          width="w-[130px]"
        >
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => toggleStatus(opt.value)}
              className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] text-text-dark hover:bg-hover transition-colors`}
            >
              {opt.label}
              {statuses.includes(opt.value) && <Check className="w-3.5 h-3.5 text-success" />}
            </button>
          ))}
          {statuses.length > 0 && (
            <button onClick={() => onStatusesChange([])} className="flex w-full items-center justify-center rounded-md px-2.5 py-1.5 text-[12px] text-text-muted hover:bg-hover border-t border-border mt-1">
              Clear
            </button>
          )}
        </FilterPill>

        {/* Attribution */}
        <FilterPill
          open={openPanel === 'attribution'}
          onToggle={() => togglePanel('attribution')}
          label={attributionLabel}
          width="w-[145px]"
        >
          {ATTRIBUTION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onAttributionChange(opt.value)
                setOpenPanel(null)
              }}
              className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                attribution === opt.value ? 'bg-hover text-text-dark font-medium' : 'text-text-dark hover:bg-hover'
              }`}
            >
              {opt.label}
              {attribution === opt.value && <Check className="w-3.5 h-3.5 text-success" />}
            </button>
          ))}
        </FilterPill>

        {/* Creator dropdown */}
        <FilterPill
          open={openPanel === 'creator'}
          onToggle={() => togglePanel('creator')}
          label={creator ?? 'Creator'}
          width="w-[140px]"
        >
          <button
            onClick={() => {
              onCreatorChange(null)
              setOpenPanel(null)
            }}
            className="flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] text-text-muted hover:bg-hover"
          >
            All creators
            {!creator && <Check className="w-3.5 h-3.5 text-success" />}
          </button>
          {filtersData?.creators.map((c) => (
            <button
              key={c}
              onClick={() => {
                onCreatorChange(c)
                setOpenPanel(null)
              }}
              className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                creator === c ? 'bg-hover text-text-dark font-medium' : 'text-text-dark hover:bg-hover'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <span className="text-xs">{filtersData.closerSet.includes(c) ? '🎯' : '🏥'}</span>
                {c}
              </span>
              {creator === c && <Check className="w-3.5 h-3.5 text-success" />}
            </button>
          ))}
        </FilterPill>

        {/* Doctor dropdown */}
        <FilterPill
          open={openPanel === 'doctor'}
          onToggle={() => togglePanel('doctor')}
          label={doctor ?? 'Doctor'}
          width="w-[150px]"
        >
          <button
            onClick={() => {
              onDoctorChange(null)
              setOpenPanel(null)
            }}
            className="flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] text-text-muted hover:bg-hover"
          >
            All doctors
            {!doctor && <Check className="w-3.5 h-3.5 text-success" />}
          </button>
          {filtersData?.doctors.map((d) => (
            <button
              key={d}
              onClick={() => {
                onDoctorChange(d)
                setOpenPanel(null)
              }}
              className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                doctor === d ? 'bg-hover text-text-dark font-medium' : 'text-text-dark hover:bg-hover'
              }`}
            >
              {d}
              {doctor === d && <Check className="w-3.5 h-3.5 text-success" />}
            </button>
          ))}
        </FilterPill>

        {/* Show tasks toggle */}
        <button
          onClick={() => onIncludeTasksChange(!includeTasks)}
          className={`flex items-center gap-2 rounded-full px-3.5 py-2 border border-border text-[12px] transition-all ${
            includeTasks ? 'bg-info/10 text-info border-info/30' : 'bg-card text-text-muted hover:bg-hover'
          }`}
        >
          <Phone className="w-3.5 h-3.5" />
          {includeTasks ? 'Tasks shown' : 'Hide tasks'}
        </button>

        {/* Column visibility (rows view only) */}
        {view === 'rows' && (
          <FilterPill
            open={openPanel === 'columns'}
            onToggle={() => togglePanel('columns')}
            icon={<SlidersHorizontal className="w-4 h-4 text-foreground flex-shrink-0" />}
            label="Columns"
            width="w-[120px]"
          >
            <div className="px-2.5 py-1.5 text-[11px] font-medium text-text-muted uppercase tracking-wide">
              Toggle columns
            </div>
            {table
              .getAllColumns()
              .filter((col) => col.getCanHide())
              .map((col) => (
                <button
                  key={col.id}
                  onClick={() => col.toggleVisibility(!col.getIsVisible())}
                  className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                    col.getIsVisible() ? 'text-text-dark' : 'text-text-muted'
                  } hover:bg-hover`}
                >
                  {COLUMN_LABELS[col.id] || col.id}
                  {col.getIsVisible() && <Check className="w-3.5 h-3.5 text-success" />}
                </button>
              ))}
          </FilterPill>
        )}
      </div>
    </div>
  )
}
